import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getApiErrorMessage = (error, fallback = "Request failed") => {
  const data = error?.response?.data || error?.data || error;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => {
        if (typeof item === "string") return item;
        const location = Array.isArray(item?.loc) ? item.loc.join(".") : "";
        return [location, item?.msg].filter(Boolean).join(": ");
      })
      .filter(Boolean)
      .join("; ");

    return message || fallback;
  }

  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }

  if (typeof data === "string" && data.trim()) return data;

  return fallback;
};

export const api = {
  // ==================== AUTH ====================
  getAdminUsers: async () => {
    const response = await axios.get(`${API_URL}/auth/admins`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createAdminUser: async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateAdminUser: async (adminId, data) => {
    const response = await axios.put(
      `${API_URL}/auth/admins/${adminId}`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  deleteAdminUser: async (adminId) => {
    const response = await axios.delete(`${API_URL}/auth/admins/${adminId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // ==================== DASHBOARD ====================
  getDashboardData: async (filters = null) => {
    const params =
      typeof filters === "string"
        ? { tower_id: filters }
        : {
            tower_id: filters?.towerId,
            device_type: filters?.deviceType,
            device_id: filters?.deviceId,
          };
    const response = await axios.get(`${API_URL}/dashboard/realtime`, {
      headers: getAuthHeader(),
      params: Object.fromEntries(
        Object.entries(params).filter(([, value]) => value),
      ),
    });
    return response.data;
  },

  getDashboardSummary: async () => {
    const response = await axios.get(`${API_URL}/dashboard/summary`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createSociety: async (data) => {
    const response = await axios.post(`${API_URL}/society/register`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateSociety: async (data) => {
    const response = await axios.put(`${API_URL}/society/`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  deleteSociety: async () => {
    const response = await axios.delete(`${API_URL}/society/`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createUGTanks: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/ug-tanks/bulkcreate`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  createSource: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/sources`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  getSources: async () => {
    const response = await axios.get(`${API_URL}/infrastructure/sources`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  updateSource: async (sourceId, data) => {
    const response = await axios.put(
      `${API_URL}/infrastructure/sources/${sourceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteSource: async (sourceId) => {
    const response = await axios.delete(
      `${API_URL}/infrastructure/sources/${sourceId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  createBulkAdditionalSensors: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/additional-sensors/bulkcreate`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getAdditionalSensors: async () => {
    const response = await axios.get(
      `${API_URL}/infrastructure/additional-sensors`,
      { headers: getAuthHeader() },
    );
    return response;
  },

  getAvailableAdditionalSensors: async () => {
    const response = await axios.get(
      `${API_URL}/sensor-groups/available-sensors`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getAdditionalSensorGroups: async () => {
    const response = await axios.get(`${API_URL}/sensor-groups`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createAdditionalSensorGroup: async (data) => {
    const response = await axios.post(`${API_URL}/sensor-groups`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updateAdditionalSensorGroup: async (groupId, data) => {
    const response = await axios.put(
      `${API_URL}/sensor-groups/${groupId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteAdditionalSensorGroup: async (groupId) => {
    const response = await axios.delete(`${API_URL}/sensor-groups/${groupId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAdditionalSensorReadings: async () => {
    const response = await axios.get(`${API_URL}/sensor-groups/readings`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAdditionalSensorHistoryDevices: async () => {
    const response = await axios.get(`${API_URL}/sensor-groups/devices`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getAdditionalSensorHistory: async ({
    startDate,
    endDate,
    deviceId = "",
    sensorId = "",
    page = 1,
    exportAll = false,
  }) => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      page: String(page),
    });
    if (exportAll) params.set("export_all", "true");
    if (deviceId) params.set("device_id", deviceId);
    if (sensorId) params.set("sensor_id", sensorId);
    const response = await axios.get(
      `${API_URL}/sensor-groups/history?${params.toString()}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getAdditionalSensorHistoryChart: async ({
    startDate,
    endDate,
    sensorId = "",
    intervalMinutes = 10,
  }) => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      interval_minutes: String(intervalMinutes),
    });
    if (sensorId) params.set("sensor_id", sensorId);
    const response = await axios.get(
      `${API_URL}/sensor-groups/history/chart?${params.toString()}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  updateAdditionalSensor: async (sensorId, data) => {
    const response = await axios.put(
      `${API_URL}/infrastructure/additional-sensors/${sensorId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteAdditionalSensor: async (sensorId) => {
    const response = await axios.delete(
      `${API_URL}/infrastructure/additional-sensors/${sensorId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  createFlowmeter: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/flowmeters`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getFlowmeters: async () => {
    const response = await axios.get(`${API_URL}/infrastructure/flowmeters`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  updateFlowmeter: async (flowmeterId, data) => {
    const response = await axios.put(
      `${API_URL}/infrastructure/flowmeters/${flowmeterId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteFlowmeter: async (flowmeterId) => {
    const response = await axios.delete(
      `${API_URL}/infrastructure/flowmeters/${flowmeterId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== TOWERS ====================

  createTowers: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/towers/bulkcreate`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  createTower: async (userId, name, location) => {
    const response = await axios.post(
      `${API_URL}/devices/towers`,
      { user_id: userId, name, location },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getTowers: async () => {
    const response = await axios.get(`${API_URL}/devices/towers`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getTower: async (towerId) => {
    const response = await axios.get(`${API_URL}/devices/towers/${towerId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  toggleTowerActive: async (towerId, active) => {
    const response = await axios.patch(
      `${API_URL}/devices/towers/${towerId}/activate?active=${active}`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteTower: async (towerId) => {
    const response = await axios.delete(
      `${API_URL}/devices/towers/${towerId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateTower: async (towerId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/towers/${towerId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== AQUATANK DEVICES ====================

  createBulkLevelControllers: async (data) => {
    const response = await axios.post(
      `${API_URL}/devices/aquatanks/bulkcreate`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  createAquaTank: async (deviceId, name) => {
    const response = await axios.post(
      `${API_URL}/devices/aquatanks`,
      { device_id: deviceId, name },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getAquaTanks: async () => {
    const response = await axios.get(`${API_URL}/devices/aquatanks`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  patchAquaTanks: async (deviceId) => {
    const response = await axios.patch(
      `${API_URL}/devices/aquatanks/${deviceId}`,
      null,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  deleteAquaTank: async (deviceId) => {
    const response = await axios.delete(
      `${API_URL}/devices/aquatanks/${deviceId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateAquaTank: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/aquatanks/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== TANKS ====================

  createBulkTanks: async (data) => {
    const response = await axios.post(
      `${API_URL}/infrastructure/tanks/bulkcreate`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  createTank: async (tankId, towerId, deviceId, name, capacity, sensorType) => {
    const response = await axios.post(
      `${API_URL}/devices/tanks`,
      {
        tank_id: tankId,
        tower_id: towerId,
        device_id: deviceId,
        name,
        capacity,
        sensor_type: sensorType,
      },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getTanks: async (towerId = null) => {
    const url = towerId
      ? `${API_URL}/devices/tanks?tower_id=${towerId}`
      : `${API_URL}/devices/tanks`;
    const response = await axios.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  deleteTank: async (tankId) => {
    const response = await axios.delete(
      `${API_URL}/infrastructure/tanks/${tankId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateTank: async (tankId, data) => {
    const response = await axios.put(
      `${API_URL}/infrastructure/tanks/${tankId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== ATBC DEVICES ====================

  createBulkPumpControllers: async (data) => {
    const response = await axios.post(
      `${API_URL}/devices/atbcs/bulkcreate`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  createATBC: async (deviceId, name) => {
    const response = await axios.post(
      `${API_URL}/devices/atbcs`,
      { device_id: deviceId, name },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getATBCs: async () => {
    const response = await axios.get(`${API_URL}/devices/atbcs`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  patchATBCs: async (deviceId) => {
    const response = await axios.patch(
      `${API_URL}/devices/atbcs/${deviceId}`,
      null,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  deleteATBC: async (deviceId) => {
    const response = await axios.delete(
      `${API_URL}/devices/atbcs/${deviceId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateATBC: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/atbcs/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== ATBPC BOREWELL PUMP CONTROLLERS ====================

  createBulkBorewellPumpControllers: async (data) => {
    const response = await axios.post(
      `${API_URL}/devices/atbpcs/bulkcreate`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteBorewellPumpController: async (deviceId) => {
    const response = await axios.delete(
      `${API_URL}/devices/atbpcs/${deviceId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateBorewellPumpController: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/atbpcs/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== PUMPS ====================

  createBulkPumps: async (data) => {
    const response = await axios.post(`${API_URL}/devices/pumps`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createPump: async (pumpId, deviceId, name, powerRating, model, control) => {
    const response = await axios.post(
      `${API_URL}/devices/pumps`,
      {
        pump_id: pumpId,
        device_id: deviceId,
        name,
        power_rating: powerRating,
        model,
        control,
      },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getPumps: async () => {
    const response = await axios.get(`${API_URL}/devices/pumps`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  deletePump: async (pumpId) => {
    const response = await axios.delete(`${API_URL}/devices/pumps/${pumpId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  updatePump: async (pumpId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/pumps/${pumpId}`,
      data,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  // ==================== VALVES ====================

  createBulkValves: async (data) => {
    const response = await axios.post(`${API_URL}/devices/valves`, data, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  createValve: async (valveId, tankId, pumpId, type, control) => {
    const response = await axios.post(
      `${API_URL}/devices/valves`,
      {
        valve_id: valveId,
        tank_id: tankId,
        pump_id: pumpId,
        type,
        control,
      },
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getValves: async (pumpId = null, tankId = null) => {
    let url = `${API_URL}/devices/valves`;
    const params = [];
    if (pumpId) params.push(`pump_id=${pumpId}`);
    if (tankId) params.push(`tank_id=${tankId}`);
    if (params.length) url += `?${params.join("&")}`;

    const response = await axios.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  deleteValve: async (valveId) => {
    const response = await axios.delete(
      `${API_URL}/devices/valves/${valveId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateValve: async (valveId, data) => {
    const response = await axios.put(
      `${API_URL}/devices/valves/${valveId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== Master Data ====================
  getSocieties: async () => {
    const response = await axios.get(`${API_URL}/society/`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getUGTanks: async () => {
    const response = await axios.get(`${API_URL}/infrastructure/ug-tanks/`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  updateUGTank: async (tankId, data) => {
    const response = await axios.put(
      `${API_URL}/infrastructure/ug-tanks/${tankId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteUGTank: async (tankId) => {
    const response = await axios.delete(
      `${API_URL}/infrastructure/ug-tanks/${tankId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getTowers: async () => {
    const response = await axios.get(`${API_URL}/infrastructure/towers`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getPumpControllers: async () => {
    const response = await axios.get(`${API_URL}/devices/atbcs`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getBorewellPumpControllers: async () => {
    const response = await axios.get(`${API_URL}/devices/atbpcs`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  patchATBPCs: async (deviceId) => {
    const response = await axios.patch(
      `${API_URL}/devices/atbpcs/${deviceId}`,
      null,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  getPumps: async () => {
    const response = await axios.get(`${API_URL}/devices/pumps`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getLevelControllers: async () => {
    const response = await axios.get(`${API_URL}/devices/aquatanks`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getTanks: async () => {
    const response = await axios.get(`${API_URL}/infrastructure/tanks`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  getValves: async () => {
    const response = await axios.get(`${API_URL}/devices/valves`, {
      headers: getAuthHeader(),
    });
    return response;
  },

  // ==================== HIERARCHY ====================
  getDeviceHierarchy: async () => {
    const response = await axios.get(`${API_URL}/devices/hierarchy`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  // ==================== ALERTS ====================
  getAlerts: async (limit = 50) => {
    const response = await axios.get(`${API_URL}/alerts?limit=${limit}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  markAlertRead: async (alertId) => {
    const response = await axios.patch(
      `${API_URL}/alerts/${alertId}/read`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getUnreadAlertCount: async () => {
    const response = await axios.get(`${API_URL}/alerts/unread-count`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getStageTanks: async () => {
    const response = await axios.get(`${API_URL}/alerts/stage-storage-assets`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getStageStorageAssets: async () => {
    const response = await axios.get(`${API_URL}/alerts/stage-storage-assets`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getStageEvents: async (assetKey, startDate, endDate, towerId = null) => {
    const params = new URLSearchParams({
      asset_key: assetKey,
      start_date: startDate,
      end_date: endDate,
    });
    if (towerId) params.append("tower_id", towerId);
    const response = await axios.get(
      `${API_URL}/alerts/stage-events?${params}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  // ==================== REPORTS ====================
  getWaterUsage: async (days = 7, towerId = null) => {
    let url = `${API_URL}/reports/water-usage?days=${days}`;
    if (towerId) url += `&tower_id=${towerId}`;
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getEnergyUsage: async (days = 7) => {
    const response = await axios.get(
      `${API_URL}/reports/energy-usage?days=${days}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getReportSummary: async (days = 7) => {
    const response = await axios.get(
      `${API_URL}/reports/summary?days=${days}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== TANK ANALYTICS - HOURLY ====================
  getUserSocieties: async () => {
    const response = await axios.get(`${API_URL}/reports/tank-societies`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getSocietyTowers: async (societyId) => {
    const response = await axios.get(
      `${API_URL}/reports/society-towers/${societyId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getAdminTowers: async () => {
    const response = await axios.get(`${API_URL}/reports/admin-towers`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getTowerTanks: async (towerId) => {
    const response = await axios.get(
      `${API_URL}/reports/tower-tanks/${towerId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getSocietyTanks: async (societyId = null) => {
    let url = `${API_URL}/reports/society-tanks`;
    if (societyId) {
      url += `?society_id=${encodeURIComponent(societyId)}`;
    }
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getSocietyStorageAssets: async (societyId = null) => {
    let url = `${API_URL}/reports/society-storage-assets`;
    if (societyId) {
      url += `?society_id=${encodeURIComponent(societyId)}`;
    }
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getTankHourlyData: async (tankId, towerId) => {
    const response = await axios.get(
      `${API_URL}/reports/tank-hourly/${tankId}?tower_id=${towerId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getTankData: async (
    tankId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/tank-data/${tankId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getStorageAssetData: async (
    assetKey,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/storage-asset-data/${encodeURIComponent(assetKey)}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getWaterFlow: async (
    tankId,
    towerId,
    view = "daily",
    date = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ view });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/water-flow/${tankId}?${params.toString()}`;

    if (view === "daily" && date) {
      url += `&date=${date}`;
    } else if ((view === "monthly" || view === "average") && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getStorageAssetWaterFlow: async (
    assetKey,
    towerId,
    view = "daily",
    date = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ view });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/storage-asset-water-flow/${encodeURIComponent(assetKey)}?${params.toString()}`;

    if (view === "daily" && date) {
      url += `&date=${date}`;
    } else if ((view === "monthly" || view === "average") && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getTowerPumps: async (towerId) => {
    const response = await axios.get(
      `${API_URL}/reports/tower-pumps/${towerId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getSocietyPumps: async (societyId = null) => {
    let url = `${API_URL}/reports/society-pumps`;
    if (societyId) {
      url += `?society_id=${encodeURIComponent(societyId)}`;
    }
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getPumpEnergy: async (
    pumpId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/pump-energy/${pumpId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getTowerValves: async (towerId) => {
    const response = await axios.get(
      `${API_URL}/reports/tower-valves/${towerId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getSocietyValves: async (societyId = null) => {
    let url = `${API_URL}/reports/society-valves`;
    if (societyId) {
      url += `?society_id=${encodeURIComponent(societyId)}`;
    }
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getValveLogs: async (
    valveId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/valve-logs/${valveId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getPumpRunningHours: async (
    pumpId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/pump-running-hours/${pumpId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getPumpLogs: async (
    pumpId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/pump-logs/${pumpId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  getValveDuration: async (
    valveId,
    towerId,
    filterType = "daily",
    startDate = null,
    endDate = null,
    month = null,
    societyId = null,
  ) => {
    const params = new URLSearchParams({ filter_type: filterType });
    if (towerId) {
      params.set("tower_id", towerId);
    }
    if (societyId) {
      params.set("society_id", societyId);
    }
    let url = `${API_URL}/reports/valve-duration/${valveId}?${params.toString()}`;

    if (filterType === "daily" && startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    } else if (filterType === "monthly" && month) {
      url += `&month=${month}`;
    }

    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  },

  // ==================== ARCHITECTURE / FLOWS ====================
  createFlow: async (flowData) => {
    const response = await axios.post(
      `${API_URL}/architecture/flows`,
      flowData,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  getFlows: async (societyId = null, towerId = null) => {
    let url = `${API_URL}/architecture/flows`;
    const params = [];
    if (societyId) params.push(`society_id=${societyId}`);
    if (towerId) params.push(`tower_id=${towerId}`);
    if (params.length) url += `?${params.join("&")}`;

    const response = await axios.get(url, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getFlow: async (flowId) => {
    const response = await axios.get(
      `${API_URL}/architecture/flows/${flowId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  updateFlow: async (flowId, flowData) => {
    const response = await axios.put(
      `${API_URL}/architecture/flows/${flowId}`,
      flowData,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  deleteFlow: async (flowId) => {
    const response = await axios.delete(
      `${API_URL}/architecture/flows/${flowId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  createBulkFlows: async (flowsData) => {
    const response = await axios.post(
      `${API_URL}/architecture/flows/bulk-create`,
      flowsData,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  // ==================== AUTOMATION ====================
  getLevelAutomationDevices: async () => {
    const response = await axios.get(`${API_URL}/automation/level/devices`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getLevelAutomation: async (deviceId) => {
    const response = await axios.get(
      `${API_URL}/automation/level/${deviceId}`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  },

  saveLevelAutomation: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/automation/level/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  publishLevelAutomation: async (deviceId) => {
    const response = await axios.post(
      `${API_URL}/automation/level/${deviceId}/publish`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  configureLevelAutomation: async (deviceId, data) => {
    const response = await axios.post(
      `${API_URL}/automation/level/${deviceId}/configure`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getPumpAutomationDevices: async () => {
    const response = await axios.get(`${API_URL}/automation/pump/devices`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getPumpAutomation: async (deviceId) => {
    const response = await axios.get(`${API_URL}/automation/pump/${deviceId}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  savePumpAutomation: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/automation/pump/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  publishPumpAutomation: async (deviceId) => {
    const response = await axios.post(
      `${API_URL}/automation/pump/${deviceId}/publish`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  configurePumpAutomation: async (deviceId, data) => {
    const response = await axios.post(
      `${API_URL}/automation/pump/${deviceId}/configure`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  getBorewellAutomationDevices: async () => {
    const response = await axios.get(`${API_URL}/automation/borewell/devices`, {
      headers: getAuthHeader(),
    });
    return response.data;
  },

  getBorewellAutomation: async (deviceId) => {
    const response = await axios.get(
      `${API_URL}/automation/borewell/${deviceId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  saveBorewellAutomation: async (deviceId, data) => {
    const response = await axios.put(
      `${API_URL}/automation/borewell/${deviceId}`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  publishBorewellAutomation: async (deviceId) => {
    const response = await axios.post(
      `${API_URL}/automation/borewell/${deviceId}/publish`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  },

  configureBorewellAutomation: async (deviceId, data) => {
    const response = await axios.post(
      `${API_URL}/automation/borewell/${deviceId}/configure`,
      data,
      { headers: getAuthHeader() },
    );
    return response.data;
  },
};
